/**
 * White noise, generated rather than downloaded.
 *
 * A few seconds of noise looped through a lowpass filter gives the whole
 * range from a hiss to distant rain, with no asset to ship and no licence to
 * honour. The filter is the "tone" control: open it and you get white noise,
 * close it and you get something closer to a fan or surf.
 */
export interface NoiseControls {
  /** Resume after the browser's autoplay block; call from a user gesture. */
  wake(): void;
  setVolume(value: number): void;
  /** 0 = deep and soft, 1 = bright and hissy. */
  setTone(value: number): void;
  stop(): void;
}

const BUFFER_SECONDS = 4;

export function startNoise(volume: number, tone: number): NoiseControls | null {
  const Ctor =
    window.AudioContext ??
    (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;

  const ctx = new Ctor();
  const buffer = ctx.createBuffer(1, ctx.sampleRate * BUFFER_SECONDS, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  // Pink noise (Paul Kellet's filter): even energy per octave, so it is soft
  // like rain rather than a hiss, yet still has the mid range that laptop and
  // phone speakers can actually play. Brown noise, used first, sat almost all
  // below 150 Hz and came out of those speakers as silence.
  let b0 = 0,
    b1 = 0,
    b2 = 0,
    b3 = 0,
    b4 = 0,
    b5 = 0,
    b6 = 0;
  for (let i = 0; i < data.length; i += 1) {
    const white = Math.random() * 2 - 1;
    b0 = 0.99886 * b0 + white * 0.0555179;
    b1 = 0.99332 * b1 + white * 0.0750759;
    b2 = 0.969 * b2 + white * 0.153852;
    b3 = 0.8665 * b3 + white * 0.3104856;
    b4 = 0.55 * b4 + white * 0.5329522;
    b5 = -0.7616 * b5 - white * 0.016898;
    data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
    b6 = white * 0.115926;
  }

  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.loop = true;

  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = toneToHz(tone);

  const gain = ctx.createGain();
  gain.gain.value = volume;

  source.connect(filter).connect(gain).connect(ctx.destination);
  source.start();

  // A context made without a click or key press (the saved mix, restored on
  // load) starts suspended and stays silent until resumed from one, so every
  // control resumes it: they are only ever called from the user's hand.
  const wake = () => {
    if (ctx.state === 'suspended') void ctx.resume();
  };

  return {
    wake,
    setVolume(value) {
      wake();
      gain.gain.setTargetAtTime(value, ctx.currentTime, 0.05);
    },
    setTone(value) {
      wake();
      filter.frequency.setTargetAtTime(toneToHz(value), ctx.currentTime, 0.05);
    },
    stop() {
      try {
        source.stop();
      } catch {
        // Already stopped; nothing to do.
      }
      void ctx.close();
    },
  };
}

/** Exponential, because pitch is heard that way and a linear slider is not. */
function toneToHz(tone: number): number {
  const clamped = Math.min(1, Math.max(0, tone));
  return 180 * Math.pow(14000 / 180, clamped);
}
